import { FGP } from './eftBuilder';

/**
 * Which fingerprint impressions a workflow collects.
 *
 * Every live-capture workflow used to take the same fourteen: ten rolled fingers, two flat thumbs and two
 * four-finger slaps. That is what an ATF or SWFT submission wants, but it is not what every agency wants —
 * the first request for something else is a workflow that collects the flat thumbs alone.
 *
 * So the set is configuration, and absent means all fourteen, which is what every existing workflow
 * expects. A workflow that names nothing keeps behaving exactly as it did.
 */

/** EBTS finger positions this product captures. 1–10 rolled, 11/12 flat thumbs, 13/14 slaps. */
export const ALL_FGPS: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];

/** FGP -> the scan type the capture flow and the builder both key impressions by. */
export const SCAN_TYPE_OF_FGP: Record<number, string> = Object.fromEntries(
  Object.entries(FGP).map(([scanType, fgp]) => [fgp, scanType]),
);

/** What an operator (and an admin choosing positions) calls each one. */
export const FGP_LABEL: Record<number, string> = {
  1: 'R thumb (rolled)', 2: 'R index (rolled)', 3: 'R middle (rolled)', 4: 'R ring (rolled)', 5: 'R little (rolled)',
  6: 'L thumb (rolled)', 7: 'L index (rolled)', 8: 'L middle (rolled)', 9: 'L ring (rolled)', 10: 'L little (rolled)',
  11: 'R thumb (flat)', 12: 'L thumb (flat)', 13: 'R four-finger slap', 14: 'L four-finger slap',
};

/**
 * The positions a workflow requires.
 *
 * An empty or absent list means all fourteen. An empty list specifically is treated as "not configured"
 * rather than "collect nothing": a workflow that captured nothing would produce an EFT with no
 * impressions at all, and that is far more likely a mis-save than an intention.
 */
export function requiredPositions(positions: number[] | undefined): number[] {
  const chosen = (positions ?? []).filter((p) => ALL_FGPS.includes(p));
  return chosen.length ? [...new Set(chosen)].sort((a, b) => a - b) : [...ALL_FGPS];
}

/** Whether this workflow takes every position — the common case, worth saying plainly in the console. */
export function isAllPositions(positions: number[] | undefined): boolean {
  return requiredPositions(positions).length === ALL_FGPS.length;
}

/**
 * Positions still outstanding: neither captured nor marked amputated/unprintable.
 *
 * Marking is a resolution, not a failure — a missing finger cannot be captured, and the submission records
 * that fact instead. This is what the capture screen must be empty of before a file can be built.
 */
export function missingPositions(
  positions: number[] | undefined,
  prints: Record<string, unknown>,
  ampCodes: Record<string, unknown>,
): number[] {
  return requiredPositions(positions).filter((fgp) => {
    const scanType = SCAN_TYPE_OF_FGP[fgp];
    if (!scanType) return false;
    return !prints[scanType] && !ampCodes[scanType];
  });
}

/** Ready to build: every required position captured or accounted for. */
export function positionsComplete(
  positions: number[] | undefined,
  prints: Record<string, unknown>,
  ampCodes: Record<string, unknown>,
): boolean {
  return missingPositions(positions, prints, ampCodes).length === 0;
}

/** "R thumb (flat) and L thumb (flat)" — for telling an operator what is still outstanding. */
export function describePositions(fgps: number[]): string {
  const names = fgps.map((f) => FGP_LABEL[f] ?? `FGP ${f}`);
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
